import classNames from 'classnames';
import gql from 'graphql-tag';
import React from 'react';
import _ from 'underscore';

import {ItemForArchive} from 'archive/ArchiveConstants';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import Tooltip from 'ui-library/bs/Tooltip';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import WarningIcon from 'ui-library/icons/WarningIcon';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import APIUtil from 'util/APIUtil';

import {InventoryArchiveSettingQuery} from './__generated/InventoryArchiveSettingQuery';

export const INVENTORY_ARCHIVE_SETTING_QUERY = gql`
  query InventoryArchiveSettingQuery @skipAudit @engComponent(name: INVENTORY) {
    inventoryConfig {
      ... on InventoryConfigNode {
        id
        enableArchiveEntityInContainerOption
      }
      ... on InaccessibleInfoNode {
        invalidId
        canRequestAccess
      }
    }
  }
`;

interface Options {
  shouldRemoveBarcodes?: boolean;
  shouldArchiveContainedSamples?: boolean;
}

export interface ArchiveStorablesOptionProps {
  items: Array<ItemForArchive>;
  onChange: (options: Options) => void;
  shouldRemoveBarcodes: boolean;
  shouldArchiveContainedSamples: boolean;
}

interface Props extends ArchiveStorablesOptionProps {
  shouldPresentArchiveEntityInContainerOption: boolean;
}

function ArchiveStorablesOption(props: Props): JSX.Element {
  // eslint-disable-next-line no-restricted-syntax
  const itemTypes = _.uniq(props.items.map((item) => APIUtil.apiIdToType(item.id)));
  const areParentStorablesPresent = itemTypes.some((itemType) =>
    [ModelTypeConstants.BOX, ModelTypeConstants.LOCATION, ModelTypeConstants.PLATE].includes(itemType)
  );
  const areContainersPresent = itemTypes.some((itemType) => itemType === ModelTypeConstants.CONTAINER);

  return (
    <div
      className={classNames({
        'u-spacing-top': BenchlingConfig.ARCHIVE_ENABLE_REMOVE_BARCODES_OPTION || areContainersPresent,
      })}
    >
      {BenchlingConfig.ARCHIVE_ENABLE_REMOVE_BARCODES_OPTION && (
        <Row>
          <Col md={12}>
            <div>
              <input
                checked={props.shouldRemoveBarcodes}
                id="shouldRemoveBarcodes"
                onChange={(e) => {
                  props.onChange({shouldRemoveBarcodes: e.target.checked});
                }}
                type="checkbox"
              />
              <span className="u-small-spacing-left">
                {props.shouldRemoveBarcodes && areParentStorablesPresent && (
                  <Tooltip title="Removing barcodes from archived inventory that contain items will also remove barcodes from the contained items.">
                    <WarningIcon className="u-tiny-spacing-right" />
                  </Tooltip>
                )}
                <FieldLabel
                  className="u-normal-font-weight"
                  htmlFor="shouldRemoveBarcodes"
                  label="Remove barcodes"
                />
              </span>
            </div>
          </Col>
        </Row>
      )}
      {props.shouldPresentArchiveEntityInContainerOption && areContainersPresent && (
        <Row>
          <Col md={12}>
            <div>
              <input
                checked={props.shouldArchiveContainedSamples}
                id="shouldArchiveContainedSamples"
                onChange={(e) => {
                  props.onChange({shouldArchiveContainedSamples: e.target.checked});
                }}
                type="checkbox"
              />
              <span className="u-small-spacing-left">
                <FieldLabel
                  className="u-normal-font-weight"
                  htmlFor="shouldArchiveContainedSamples"
                  label="Archive entities in containers"
                />
              </span>
            </div>
          </Col>
          {props.shouldArchiveContainedSamples && (
            <Row>
              <Col md={12}>
                <div className="u-small-spacing-top u-small-spacing-left u-small-spacing-right">
                  <InlineBanner
                    bannerType={InlineBannerType.INFO}
                    text="If an entity is stored in multiple containers, archiving the entity archives it in other containers."
                  />
                </div>
              </Col>
            </Row>
          )}
        </Row>
      )}
    </div>
  );
}

function QueryingArchiveStorablesOption(props: ArchiveStorablesOptionProps): JSX.Element {
  const queryResult = useBenchlingQuery<InventoryArchiveSettingQuery>(INVENTORY_ARCHIVE_SETTING_QUERY);
  if (queryResult.type === QueryResultType.PENDING) {
    return <HorizontalThrobber />;
  }
  let shouldPresentArchiveEntityInContainerOption = false;
  if (queryResult.type === QueryResultType.SUCCESS && !isInaccessible(queryResult.data.inventoryConfig)) {
    shouldPresentArchiveEntityInContainerOption = Boolean(
      queryResult.data.inventoryConfig.enableArchiveEntityInContainerOption
    );
  }
  return (
    <ArchiveStorablesOption
      {...props}
      shouldPresentArchiveEntityInContainerOption={shouldPresentArchiveEntityInContainerOption}
    />
  );
}

QueryingArchiveStorablesOption.isEnabled = (items: Array<ItemForArchive>): boolean =>
  items.some((item) => APIUtil.isStorable(item.id));
QueryingArchiveStorablesOption.getDefaultState = (): Options => ({
  shouldRemoveBarcodes: false,
  shouldArchiveContainedSamples: false,
});

export default QueryingArchiveStorablesOption;
